/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        High-Performance React Virtualizer Hook (Task 10.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

export interface VirtualizerOptions {
  count: number;
  getScrollElement: () => HTMLElement | null;
  estimateSize: (index: number) => number;
  overscan?: number;
  gap?: number;
}

export interface VirtualItem {
  key: number;
  index: number;
  start: number;
  size: number;
  end: number;
}

export interface Virtualizer {
  getVirtualItems: () => VirtualItem[];
  getTotalSize: () => number;
  scrollToIndex: (index: number, options?: { align?: 'start' | 'center' | 'end' | 'auto' }) => void;
}

/**
 * Lightweight, zero-dependency virtualizer hook compatible with @tanstack/react-virtual API.
 * Guarantees smooth 60fps rendering for lists with thousands of bilingual segments.
 */
export function useVirtualizer({
  count,
  getScrollElement,
  estimateSize,
  overscan = 5,
  gap = 0,
}: VirtualizerOptions): Virtualizer {
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(800);
  const measuredSizes = useRef<Map<number, number>>(new Map());

  // Handle scroll and resize events
  useEffect(() => {
    const element = getScrollElement();
    if (!element) return;

    const handleScroll = () => {
      setScrollTop(element.scrollTop);
    };

    const handleResize = () => {
      setViewportHeight(element.clientHeight || 800);
    };

    handleResize();
    handleScroll();

    element.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      element.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [getScrollElement]);

  // Compute item offsets and positions
  const { itemOffsets, totalSize } = useMemo(() => {
    const offsets: number[] = new Array(count);
    let currentOffset = 0;

    for (let i = 0; i < count; i++) {
      offsets[i] = currentOffset;
      const size = measuredSizes.current.get(i) ?? estimateSize(i);
      currentOffset += size + gap;
    }

    return {
      itemOffsets: offsets,
      totalSize: Math.max(0, currentOffset - gap),
    };
  }, [count, estimateSize, gap]);

  // Compute currently visible virtual items with overscan buffer
  const getVirtualItems = useCallback((): VirtualItem[] => {
    if (count === 0) return [];

    let startIndex = 0;
    let endIndex = count - 1;

    // Binary search for visible start index
    let low = 0;
    let high = count - 1;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (itemOffsets[mid] <= scrollTop) {
        startIndex = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    // Binary search for visible end index
    const visibleBottom = scrollTop + viewportHeight;
    low = startIndex;
    high = count - 1;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (itemOffsets[mid] <= visibleBottom) {
        endIndex = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    // Apply overscan buffer
    const bufferedStart = Math.max(0, startIndex - overscan);
    const bufferedEnd = Math.min(count - 1, endIndex + overscan);

    const items: VirtualItem[] = [];
    for (let i = bufferedStart; i <= bufferedEnd; i++) {
      const start = itemOffsets[i];
      const size = measuredSizes.current.get(i) ?? estimateSize(i);
      items.push({
        key: i,
        index: i,
        start,
        size,
        end: start + size,
      });
    }

    return items;
  }, [count, itemOffsets, scrollTop, viewportHeight, overscan, estimateSize]);

  // Scroll to index with alignment options
  const scrollToIndex = useCallback(
    (index: number, options?: { align?: 'start' | 'center' | 'end' | 'auto' }) => {
      const element = getScrollElement();
      if (!element || index < 0 || index >= count) return;

      const itemStart = itemOffsets[index];
      const itemSize = measuredSizes.current.get(index) ?? estimateSize(index);
      const align = options?.align ?? 'auto';

      let targetScrollTop = itemStart;

      if (align === 'center') {
        targetScrollTop = itemStart - viewportHeight / 2 + itemSize / 2;
      } else if (align === 'end') {
        targetScrollTop = itemStart - viewportHeight + itemSize;
      } else if (align === 'auto') {
        if (itemStart < element.scrollTop) {
          targetScrollTop = itemStart;
        } else if (itemStart + itemSize > element.scrollTop + viewportHeight) {
          targetScrollTop = itemStart - viewportHeight + itemSize;
        } else {
          return; // Already in view
        }
      }

      element.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth',
      });
    },
    [count, getScrollElement, itemOffsets, viewportHeight, estimateSize]
  );

  return {
    getVirtualItems,
    getTotalSize: () => totalSize,
    scrollToIndex,
  };
}
